import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import {
  Download, Database, HardDrive, Clock, CheckCircle,
  AlertTriangle, RefreshCw, ArrowLeft, Shield, Package,
  Code, Settings, ToggleLeft, ToggleRight, Save, FileText,
} from 'lucide-react';
import toast from 'react-hot-toast';

const fmt = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024, s = ['B','KB','MB','GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + s[i];
};

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day:'2-digit', month:'2-digit', year:'numeric',
      hour:'2-digit', minute:'2-digit',
    });
  } catch { return iso; }
};

function InfoCard({ emoji, title, sub }) {
  return (
    <div style={{
      padding: '14px 16px',
      background: 'var(--bg)', borderRadius: 10,
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: '1.4rem', marginBottom: 6 }}>{emoji}</div>
      <div style={{ fontWeight: 700, fontSize: '.85rem', marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{sub}</div>
    </div>
  );
}

export default function AdminBackup() {
  const { can } = useAuth();
  const nav = useNavigate();

  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [tab, setTab] = useState('backup'); // 'backup' | 'maintenance'

  // Wartungsmodus
  const [maintenance, setMaintenance] = useState({ active: false, message: '' });
  const [maintSaving, setMaintSaving] = useState(false);

  useEffect(() => {
    if (!can('benutzer_manage')) nav('/dashboard');
  }, [can, nav]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/backup/status');
      setStatus(data);
      if (data.maintenance) {
        setMaintenance({
          active:  !!data.maintenance.active,
          message: data.maintenance.message || 'Das System befindet sich in der Wartung.',
        });
      }
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);

  const createBackup = async () => {
    setCreating(true);
    try {
      toast('⏳ Backup wird erstellt...', { duration: 2000 });
      const { data } = await api.post('/admin/backup/create');
      toast.success(`✅ Backup erstellt (${fmt(data.size)})`);
      setStatus(prev => ({
        ...prev,
        lastBackup: data.timestamp,
        backupFile: data.filename,
        backupSize: data.size,
      }));
    } catch(e) {
      toast.error(e.response?.data?.error || 'Backup fehlgeschlagen');
    } finally { setCreating(false); }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/backup/download', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(await response.text());
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const cd   = response.headers.get('Content-Disposition') || '';
      const m    = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `fuhrpark-backup-${new Date().toISOString().slice(0,10)}.zip`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Download gestartet');
    } catch(e) { toast.error('Download fehlgeschlagen: ' + e.message); }
    finally { setDownloading(false); }
  };

  const saveMaintenance = async () => {
    setMaintSaving(true);
    try {
      await api.put('/admin/maintenance', {
        active:  maintenance.active,
        message: maintenance.message,
      });
      toast.success(maintenance.active ? '⚠️ Wartungsmodus AKTIVIERT' : '✅ Wartungsmodus deaktiviert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setMaintSaving(false); }
  };

  return (
    <Layout title="Administration">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 20, gap: 6, paddingLeft: 4 }}
        onClick={() => nav('/einstellungen')}>
        <ArrowLeft size={16}/> Zurück
      </button>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab-btn ${tab==='backup'?'active':''}`} onClick={() => setTab('backup')}>
          💾 Datensicherung
        </button>
        <button className={`tab-btn ${tab==='maintenance'?'active':''}`} onClick={() => setTab('maintenance')}>
          🔧 Wartungsmodus
        </button>
      </div>

      {/* ── BACKUP TAB ── */}
      {tab === 'backup' && (
        <>
          {/* Was wird gesichert */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head">
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36,height:36,borderRadius:9,background:'var(--blue-bg)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                  <Shield size={18} color="var(--blue)"/>
                </div>
                <div>
                  <div style={{ fontWeight:800, fontSize:'1rem' }}>Vollständiges Backup</div>
                  <div style={{ fontSize:'.75rem', color:'var(--text-muted)' }}>Datenbank, Uploads, Konfiguration & Quellcode</div>
                </div>
              </div>
            </div>
            <div style={{ padding:'16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              <InfoCard emoji="🗄️" title="Datenbank" sub="fuhrpark.db — Fahrzeuge, Schäden, Termine, Benutzer, Packlisten"/>
              <InfoCard emoji="📁" title="Uploads" sub="Fahrzeugbilder, Fahrzeugscheine, Schadensbilder"/>
              <InfoCard emoji="⚙️" title="Konfiguration" sub="nginx.conf, docker-compose.yml, .env, Dockerfiles"/>
              <InfoCard emoji="💻" title="Quellcode" sub="Frontend & Backend (ohne node_modules/build)"/>
            </div>

            <div style={{ padding:'0 16px 16px', display:'flex', gap:10, flexWrap:'wrap' }}>
              <button
                className="btn btn-primary"
                onClick={createBackup}
                disabled={creating}
                style={{ flex:'1 1 200px', justifyContent:'center', padding:'13px 20px', fontSize:'.92rem' }}>
                {creating
                  ? <><div className="spinner" style={{width:16,height:16}}/> Wird erstellt...</>
                  : <><Database size={16}/> Backup jetzt erstellen</>
                }
              </button>

              {status?.backupFile && (
                <button
                  className="btn btn-secondary"
                  onClick={downloadBackup}
                  disabled={downloading}
                  style={{ flex:'1 1 200px', justifyContent:'center', padding:'13px 20px', fontSize:'.92rem' }}>
                  {downloading
                    ? <><div className="spinner" style={{width:16,height:16}}/> Lädt herunter...</>
                    : <><Download size={16}/> Backup herunterladen</>
                  }
                </button>
              )}
            </div>
          </div>

          {/* Letztes Backup */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-head">
              <span className="card-title">Letztes Backup</span>
              <button className="btn btn-ghost btn-sm" onClick={loadStatus} disabled={loading}>
                <RefreshCw size={13}/> {loading ? '...' : 'Aktualisieren'}
              </button>
            </div>
            <div style={{ padding:'16px' }}>
              {loading ? (
                <div className="loading-box" style={{padding:20}}><div className="spinner"/></div>
              ) : status?.lastBackup ? (
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <CheckCircle size={22} color="var(--green)" style={{flexShrink:0}}/>
                  <div>
                    <div style={{ fontWeight:700, fontSize:'.92rem' }}>{fmtDate(status.lastBackup)}</div>
                    <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:3, fontFamily:'monospace' }}>
                      {status.backupFile}
                      {status.backupSize ? ` · ${fmt(status.backupSize)}` : ''}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <AlertTriangle size={22} color="var(--yellow)" style={{flexShrink:0}}/>
                  <div style={{ color:'var(--text-muted)', fontSize:'.88rem' }}>Noch kein Backup erstellt</div>
                </div>
              )}
            </div>
          </div>

          {/* System-Info */}
          {status && (
            <div className="card" style={{ marginBottom:16 }}>
              <div className="card-head"><span className="card-title">System-Informationen</span></div>
              {[
                { icon:<Database size={15}/>, label:'Datenbankgröße', value: fmt(status.dbSize) },
                { icon:<HardDrive size={15}/>, label:'Upload-Dateien', value: `${status.uploadCount || 0} Dateien · ${fmt(status.uploadSize)}` },
                { icon:<Clock size={15}/>, label:'Server-Zeit', value: fmtDate(status.serverTime) },
                { icon:<Shield size={15}/>, label:'Datenbankversion', value: status.dbVersion || 'SQLite 3' },
              ].map(({ icon, label, value }, i, arr) => (
                <div key={label} style={{
                  display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
                  borderBottom: i < arr.length-1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ width:32,height:32,borderRadius:8,background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text-muted)',flexShrink:0 }}>
                    {icon}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'.75rem', color:'var(--text-muted)', fontWeight:600 }}>{label}</div>
                    <div style={{ fontSize:'.88rem', fontWeight:600, marginTop:1 }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Hinweis */}
          <div style={{
            padding:'14px 16px', background:'var(--blue-bg)', borderRadius:12,
            border:'1px solid rgba(37,99,235,.15)', fontSize:'.78rem', color:'var(--blue)', lineHeight:1.7,
          }}>
            <strong>💡 Empfehlung:</strong> Backups wöchentlich erstellen und auf einem externen Speicher sichern.
            Das ZIP enthält alles für eine vollständige Wiederherstellung — inkl. Anleitung (README.md).
          </div>
        </>
      )}

      {/* ── WARTUNGSMODUS TAB ── */}
      {tab === 'maintenance' && (
        <>
          {/* Status Banner */}
          <div style={{
            padding:'16px 18px',
            marginBottom: 16,
            background: maintenance.active ? 'var(--red-bg)' : 'var(--green-bg)',
            borderRadius: 12,
            border: `1px solid ${maintenance.active ? 'rgba(220,38,38,.2)' : 'rgba(22,163,74,.2)'}`,
            display:'flex', alignItems:'center', gap:14,
          }}>
            <div style={{ fontSize:'2rem' }}>{maintenance.active ? '🔧' : '✅'}</div>
            <div>
              <div style={{ fontWeight:800, fontSize:'1rem', color: maintenance.active ? 'var(--red)' : 'var(--green)' }}>
                Wartungsmodus {maintenance.active ? 'AKTIV' : 'INAKTIV'}
              </div>
              <div style={{ fontSize:'.78rem', color:'var(--text-muted)', marginTop:2 }}>
                {maintenance.active
                  ? 'Nur Administratoren können sich einloggen und arbeiten.'
                  : 'Alle Benutzer haben normalen Zugang.'
                }
              </div>
            </div>
          </div>

          {/* Einstellungen */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-head">
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36,height:36,borderRadius:9,background:'var(--yellow-bg)',display:'flex',alignItems:'center',justifyContent:'center' }}>
                  <Settings size={18} color="var(--yellow)"/>
                </div>
                <span className="card-title">Wartungsmodus konfigurieren</span>
              </div>
            </div>
            <div style={{ padding:'16px', display:'flex', flexDirection:'column', gap:16 }}>

              {/* Toggle */}
              <div style={{
                display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'14px 16px', background:'var(--bg)', borderRadius:10,
                border:'1px solid var(--border)',
              }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'.9rem' }}>Wartungsmodus</div>
                  <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginTop:2 }}>
                    Wenn aktiv, können nur Admins die App nutzen
                  </div>
                </div>
                <button
                  onClick={() => setMaintenance(p => ({...p, active: !p.active}))}
                  style={{
                    width:54, height:30, borderRadius:15,
                    border: '2px solid',
                    borderColor: maintenance.active ? 'var(--red)' : 'var(--border)',
                    background: maintenance.active ? 'var(--red)' : 'var(--bg-input)',
                    position:'relative', cursor:'pointer', flexShrink:0,
                    transition:'all 280ms', padding:0,
                  }}>
                  <div style={{
                    position:'absolute', top:2,
                    left: maintenance.active ? 26 : 2,
                    width:22, height:22, background:'#fff', borderRadius:'50%',
                    transition:'left 280ms cubic-bezier(.4,0,.2,1)',
                    boxShadow:'0 1px 4px rgba(0,0,0,.3)',
                  }}/>
                </button>
              </div>

              {/* Meldungstext */}
              <div className="f-group">
                <label className="f-label">Meldung für Benutzer</label>
                <textarea
                  className="f-input"
                  rows={3}
                  value={maintenance.message}
                  onChange={e => setMaintenance(p => ({...p, message: e.target.value}))}
                  placeholder="Das System befindet sich in der Wartung. Bitte versuchen Sie es später erneut."
                />
                <div style={{ fontSize:'.72rem', color:'var(--text-muted)' }}>
                  Diese Meldung sehen Benutzer wenn sie sich einloggen möchten.
                </div>
              </div>

              {/* Speichern */}
              <button
                className="btn btn-primary"
                onClick={saveMaintenance}
                disabled={maintSaving}
                style={{ alignSelf:'flex-start', padding:'11px 24px' }}>
                {maintSaving
                  ? <><div className="spinner" style={{width:14,height:14}}/> Wird gespeichert...</>
                  : <><Save size={15}/> Einstellungen speichern</>
                }
              </button>
            </div>
          </div>

          {/* Was passiert im Wartungsmodus */}
          <div className="card">
            <div className="card-head"><span className="card-title">Was passiert im Wartungsmodus?</span></div>
            <div style={{ padding:'4px 0' }}>
              {[
                { emoji:'🔒', text:'Nicht-Admin-Benutzer sehen eine Wartungsseite beim Login' },
                { emoji:'👑', text:'Administratoren können weiterhin normal arbeiten' },
                { emoji:'📵', text:'Alle API-Anfragen von Nicht-Admins werden blockiert' },
                { emoji:'🔔', text:'Laufende Sessions von Benutzer werden unterbrochen' },
                { emoji:'⚡', text:'Sofort aktiv — kein Neustart notwendig' },
              ].map(({ emoji, text }, i, arr) => (
                <div key={i} style={{
                  display:'flex', alignItems:'center', gap:12,
                  padding:'12px 16px',
                  borderBottom: i < arr.length-1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span style={{ fontSize:'1.2rem', flexShrink:0 }}>{emoji}</span>
                  <span style={{ fontSize:'.85rem' }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
